// eslint-disable-next-line no-restricted-imports
import { Transition } from "@headlessui/react";
import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import { t } from "i18n/i18n";
import { DownloadCSVButton } from "mds/components/DownloadCSVButton";
import { TooltipTrigger } from "mds/components/TooltipTrigger";
import { ChevronRightIcon } from "mds/icons";
import { type CSVData } from "utils/file";

interface ScrollableTableContainerProps {
  title?: string;
  titleTooltipText?: string;
  className?: string;
  children: ReactNode;
  exportData?: CSVData | (() => Promise<CSVData>);
}

export const ScrollableTableContainer: FC<ScrollableTableContainerProps> = ({
  title,
  titleTooltipText,
  className,
  children,
  exportData,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [hasScrollX, setHasScrollX] = useState(false);
  const [isAtScrollEnd, setIsAtScrollEnd] = useState(true);

  const scrollToRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        left: scrollContainerRef.current.scrollWidth,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const el = scrollContainerRef?.current;
    if (!el) return;

    const check = () => {
      setHasScrollX(el.scrollWidth > el.clientWidth);
      setIsAtScrollEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
    };

    check();

    el.addEventListener("scroll", check);

    const resizeObserver = new ResizeObserver(check);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener("scroll", check);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div className={twMerge("mb-8", className)}>
      {title && (
        <div className="mb-4 flex w-full items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-black-tint-40">{title}</h3>
            {titleTooltipText && <TooltipTrigger content={titleTooltipText} />}
          </div>

          {exportData && (
            <div className="flex items-center gap-2">
              <DownloadCSVButton csvData={exportData} text={t("common.export")} title={title} />
            </div>
          )}
        </div>
      )}

      <div className="relative">
        <div
          className="overflow-x-auto overflow-y-hidden whitespace-nowrap"
          ref={scrollContainerRef}
        >
          {children}
        </div>

        <Transition show={hasScrollX && !isAtScrollEnd}>
          <button
            aria-label={t("common.scroll_to_far_right")}
            className="group absolute inset-y-0 right-0 z-[1] flex w-10 items-center justify-center bg-gradient-to-l from-white/100 via-white/80 to-white/0 transition duration-500 data-[closed]:opacity-0"
            type="button"
            onClick={scrollToRight}
          >
            <ChevronRightIcon className="text-black-tint-20 group-hover:animate-bounce" />
          </button>
        </Transition>
      </div>
    </div>
  );
};
